import {
  autoUpdate,
  FloatingFocusManager,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ColorType, FormInput, Icon, Label, LabelType } from '..';
import { CancelClose, ChevronDown, Search } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { getCurrentTheme } from '../../../stores/selectors/theme.selector';
import {
  SITE_TYPE_CATEGORY_SUBCATEGORIES,
  SiteType,
  SiteTypeCategory,
  SiteTypeSubcategory,
} from '../../../stores/types/sitetype.interface';

interface SiteTypeDropdownProps {
  value?: SiteType;
  onChange: (siteType: SiteType | undefined) => void;
  disabled?: boolean;
  dataTestId?: string;
}

interface SiteTypeOption {
  siteType: SiteType;
  displayText: string;
}

interface SiteTypeGroup {
  category: SiteTypeCategory;
  displayText: string;
  options: SiteTypeOption[];
}

const DROPDOWN_MIN_HEIGHT = 400;
const DROPDOWN_MAX_HEIGHT = 500;
const LIST_MAX_HEIGHT = 280;

const shouldShowOtherDescription = (siteType: SiteType | undefined): boolean =>
  siteType?.subcategory === SiteTypeSubcategory.OTHER_NOT_LISTED;

export const SiteTypeDropdown = memo(
  ({ value, onChange, disabled = false, dataTestId = '' }: SiteTypeDropdownProps) => {
    const { t } = useTranslation();
    const theme = useSelector(getCurrentTheme);
    const [isOpen, setIsOpen] = useState(false);
    const [searchStr, setSearchStr] = useState('');

    const onOpenChange = useCallback((open: boolean) => {
      if (!open) setSearchStr('');
      setIsOpen(open);
    }, []);

    const { refs, floatingStyles, context } = useFloating({
      placement: 'top-start',
      open: isOpen,
      onOpenChange,
      middleware: [
        offset(10),
        size({
          apply({ rects, elements, availableHeight }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
              maxHeight: `${Math.max(
                DROPDOWN_MIN_HEIGHT,
                Math.min(availableHeight, DROPDOWN_MAX_HEIGHT),
              )}px`,
            });
          },
          padding: 10,
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    const selectedLabel = value
      ? t(`site_type.subcategories.${value.subcategory.toLowerCase()}`)
      : null;

    const groupedOptions = useMemo(() => {
      const groups: SiteTypeGroup[] = Object.entries(SITE_TYPE_CATEGORY_SUBCATEGORIES).map(
        ([category, subcategories]) => ({
          category: category as SiteTypeCategory,
          displayText: t(`site_type.categories.${(category as string).toLowerCase()}`),
          options: subcategories.map((subcategory) => ({
            siteType: { category: category as SiteTypeCategory, subcategory },
            displayText: t(`site_type.subcategories.${subcategory.toLowerCase()}`),
          })),
        }),
      );

      if (!searchStr) return groups;

      const lower = searchStr.toLowerCase();
      return groups
        .map((group) => ({
          ...group,
          options: group.options.filter((opt) => opt.displayText.toLowerCase().includes(lower)),
        }))
        .filter((group) => group.options.length > 0);
    }, [t, searchStr]);

    const handleSelect = useCallback(
      (option: SiteType) => {
        if (shouldShowOtherDescription(option)) {
          onChange({
            category: SiteTypeCategory.OTHER,
            subcategory: SiteTypeSubcategory.OTHER_NOT_LISTED,
            otherDescription: '',
          });
        } else {
          onChange(option);
        }
        onOpenChange(false);
      },
      [onChange, onOpenChange],
    );

    const handleOtherDescriptionChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          category: SiteTypeCategory.OTHER,
          subcategory: SiteTypeSubcategory.OTHER_NOT_LISTED,
          otherDescription: event.target.value,
        });
      },
      [onChange],
    );

    const setFloatingRef = useCallback(
      (node: HTMLDivElement | null) => {
        refs.setFloating(node);
        if (node) {
          node.querySelector<HTMLInputElement>('#site-type-search-input')?.focus({
            preventScroll: true,
          });
        }
      },
      [refs],
    );

    const isSelected = value?.category !== undefined;

    return (
      <div className='flex flex-col gap-2'>
        <Label text={t('site_type.label')} type={LabelType.LABEL_M} color={ColorType.BLACK} />

        <div ref={refs.setReference} {...getReferenceProps()}>
          <button
            type='button'
            className={`w-full h-[40px] pl-3 pr-2 py-2.5 rounded border-grey3 bg-grey1 ${
              isOpen ? 'bg-grey2' : 'hover:bg-grey2'
            }`}
            disabled={disabled}
            data-testid={dataTestId}
          >
            <div className='flex items-center justify-between w-full h-full'>
              <Label
                text={selectedLabel || t('site_type.placeholder')}
                type={LabelType.BODY3}
                color={selectedLabel ? ColorType.BLACK : ColorType.GREY4}
                className='truncate text-left'
              />
              <Icon
                className={`ml-4 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                src={ChevronDown}
                color={isSelected ? ColorType.GREY6 : ''}
              />
            </div>
          </button>
        </div>

        {isOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              className='z-50 flex flex-col p-2 bg-white outline-0 rounded-lg list-shadow overflow-hidden'
              ref={setFloatingRef}
              style={floatingStyles}
              {...getFloatingProps()}
              data-testid='siteTypeOptionWrapper'
            >
              <div className='sticky top-0 z-10 bg-white'>
                <FormInput
                  width='100%'
                  placeholder={t('search')}
                  icon={Search}
                  iconAlt={t('search')}
                  iconSize={IconSize.SIZE_20x20}
                  onChange={(event: any) => setSearchStr(event.target.value)}
                  showOutline={false}
                  suffixIcon={searchStr ? CancelClose : ''}
                  suffixIconSize={IconSize.SIZE_20x20}
                  defaultValue={searchStr}
                  onClickSuffix={() => setSearchStr('')}
                  inputId='site-type-search-input'
                  dataTestId='siteTypeSearchInput'
                />
              </div>

              <div
                className='flex flex-col flex-1 min-h-0 overflow-y-auto pb-1'
                style={{ maxHeight: LIST_MAX_HEIGHT }}
              >
                {groupedOptions.map(({ category, displayText: groupDisplayText, options }) => (
                  <div key={category}>
                    <div className='px-2 pt-3 pb-1'>
                      <Label
                        text={groupDisplayText}
                        type={LabelType.LABEL_XS}
                        color={ColorType.GREY5}
                        className='uppercase'
                      />
                    </div>
                    {options.map(({ siteType, displayText: optionDisplayText }) => {
                      const active =
                        value?.category === siteType.category &&
                        value?.subcategory === siteType.subcategory;
                      return (
                        <button
                          key={siteType.subcategory}
                          type='button'
                          className={`hover:bg-grey1 pl-4 pr-2 min-h-[40px] text-left w-full ${
                            active ? 'bg-grey1 rounded' : ''
                          }`}
                          onClick={() => handleSelect(siteType)}
                          data-testid='siteTypeOptionItem'
                        >
                          <Label
                            type={LabelType.BODY3}
                            text={optionDisplayText}
                            color={active ? theme.colorPalettes.primary : ColorType.BLACK}
                            className='truncate'
                          />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </FloatingFocusManager>
        )}

        {shouldShowOtherDescription(value) && (
          <div className='flex flex-col gap-1'>
            <Label
              text={t('site_type.please_specify')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
            <FormInput
              inputId='site_type_other_description'
              placeholder={t('site_type.please_specify_placeholder')}
              onChange={handleOtherDescriptionChange}
              defaultValue={value?.otherDescription || ''}
              width='100%'
              dataTestId='siteTypeOtherDescription'
            />
          </div>
        )}
      </div>
    );
  },
);

SiteTypeDropdown.displayName = 'SiteTypeDropdown';
